import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Target, Eye, Heart, Users,
  ArrowRight, Lightbulb, Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/PageSEO";

const values = [
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "We stay ahead of technology trends to deliver cutting-edge solutions."
  },
  {
    icon: Shield,
    title: "Reliability",
    description: "Count on us for consistent, secure, and dependable IT services."
  },
  {
    icon: Heart,
    title: "Client Focus",
    description: "Your success is our priority. We listen, adapt, and deliver."
  },
  {
    icon: Users,
    title: "Collaboration",
    description: "We work as an extension of your team, not just a vendor."
  }
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="About Us — IT Services Company in Lowestoft, Suffolk"
        description="Learn about Your-IT-Solutions — based in Lowestoft, Suffolk, helping businesses nationwide with website building, AI automation, Microsoft 365, and strategic IT consulting."
        path="/about"
      />
      {/* Hero Section */}
      <section className="py-20 lg:py-32 hero-gradient relative overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              About Us
            </span>
            <h1 
              data-testid="about-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
            >
              Building Technology That <span className="gradient-text">Empowers Business</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed">
              Your-IT-Solutions was founded with a simple mission: to make enterprise-grade 
              IT solutions accessible to businesses of all sizes. We believe technology 
              should enable growth, not create barriers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 lg:py-32 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 lg:p-10 rounded-2xl bg-gray-900/50 border border-gray-800"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-6">
                <Target size={24} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-400 leading-relaxed">
                To deliver innovative, reliable, and scalable IT solutions that help 
                businesses thrive in an increasingly digital world. We're committed to 
                being the technology partner our clients can always count on.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-8 lg:p-10 rounded-2xl bg-gray-900/50 border border-gray-800"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center mb-6">
                <Eye size={24} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-gray-400 leading-relaxed">
                To become the most trusted IT solutions partner for growing businesses, 
                known for our technical excellence, customer-centric approach, and 
                unwavering commitment to quality.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
                Our Story
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                From Side Projects to Your Trusted Partner
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  After spending over 10 years working in the IT industry, supporting businesses 
                  across a wide range of sectors, we gained a strong understanding of how important 
                  reliable technology and a professional online presence are for modern businesses.
                </p>
                <p>
                  Alongside our work in IT, we regularly helped family, friends, and local businesses 
                  build websites. What started as small side projects quickly grew through word of mouth, 
                  as more people wanted simple, professional websites that represented their business 
                  properly online.
                </p>
                <p>
                  Seeing the demand for affordable, well-built websites, we decided to take the next 
                  step and start our own business. Today we focus on creating clean, modern websites 
                  while providing the support and reliability businesses need to maintain their presence 
                  online with confidence.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-32 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              What Drives Us Forward
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              These core values guide every decision we make and every solution we deliver.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-card-${value.title.toLowerCase()}`}
                className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon size={24} className="text-cyan-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                <p className="text-gray-400 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gray-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Work with Us?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Let's discuss how Your-IT-Solutions can help transform your business technology.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  data-testid="about-cta-contact"
                  size="lg"
                  className="btn-primary text-white font-medium px-8 h-12"
                >
                  Contact Us <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  data-testid="about-cta-services"
                  variant="outline"
                  size="lg"
                  className="border-gray-700 text-white hover:bg-white/5 font-medium px-8 h-12"
                >
                  View Services
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
